import { useState, useEffect } from 'react';
import { api } from '@/services/api';
import { transformFormData } from '@/features/application-form/form-transform';
import { LAYOUT_DEFAULTS, resolveLayout } from '@/config/layoutDefaults';
import { applyLocalLayoutOverrides } from '@/config/localLayoutOverrides';

/**
 * Hook for loading form and site configurations
 *
 * @param {string} formGuid - The form GUID from the URL routing parameter
 * @returns {{
 *   formConfig: object|null,
 *   siteConfig: object|null,
 *   layout: object,
 *   loading: boolean,
 *   error: string|null
 * }}
 */
export const useFormFields = (formGuid) => {
  const [formConfig, setFormConfig] = useState(null);
  const [siteConfig, setSiteConfig] = useState(null);
  // Defaults are used until the site configuration provides its overrides.
  const [layout, setLayout] = useState(LAYOUT_DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const abortController = new AbortController();
    let isCancelled = false;

    const fetchConfigurations = async () => {
      if (!formGuid) {
        setError('Form GUID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const [formResponse, siteResponse] = await Promise.all([
          api.getFormConfig(formGuid, abortController.signal),
          api.getSiteConfig(formGuid, abortController.signal),
        ]);

        if (isCancelled) return;

        if (!formResponse.success) {
          setError(formResponse.error || 'Failed to load form configuration');
          return;
        }

        if (!siteResponse.success) {
          setError(siteResponse.error || 'Failed to load site configuration');
          return;
        }

        const transformedFormConfig = transformFormData(formResponse.data);

        setFormConfig({
          ...transformedFormConfig,
          title: siteResponse.data.title,
          description: siteResponse.data.description,
        });
        setSiteConfig(siteResponse.data);
        // In development a local, git-ignored file can override the backend values.
        setLayout(resolveLayout(applyLocalLayoutOverrides(siteResponse.data.layout)));
      } catch (err) {
        if (err.name === 'AbortError' || isCancelled) return;
        setError('Failed to load configurations');
        console.error('Error loading configurations:', err);
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    fetchConfigurations();

    return () => {
      isCancelled = true;
      abortController.abort();
    };
  }, [formGuid]);

  return {
    config: formConfig,
    formConfig,
    siteConfig,
    layout,
    loading,
    error
  };
};
